import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@core/service/auth.service';
import { RestClientService } from '@core/service/rest-client.service';
import { UtilitiesService } from '@core/service/utilities.service';

@Component({
  selector: 'app-benef-info',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './benef-info.component.html',
  styleUrl: './benef-info.component.css'
})
export class BenefInfoComponent {
  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;
  
  // Form properties
  input_option = 'MAT';
  searchPlaceHolder = 'Entrez numéro matricule';
  input_value: any;
  isLoading = false;
  searchAttempted = false;
  
  // Beneficiary data
  itemBeneficiare: any = {};
  
  // Image handling properties
  imageLoading = false;
  canEditProfile = false; // Set based on user permissions
  defaultImages: string[] = [
    'https://images.unsplash.com/photo-1494790108755-2616b612b5bb?w=200&h=200&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=face'
  ];
  
  // Component inputs/outputs
  @Output() sendUserInfo = new EventEmitter<any>();
  @Input() beneficiaireAutOselect: any;
  
  // User info
  user: any;

  constructor(
    private restClient: RestClientService,
    private router: Router,
    private authService: AuthService,
    private utilities: UtilitiesService
  ) {
    this.user = this.authService.getCurrentUser();
    this.canEditProfile = this.checkEditPermissions();
  }

  /**
   * Check if user can edit profile images
   */
  private checkEditPermissions(): boolean {
    // Add your permission logic here
    return this.user?.role === 'admin' || this.user?.permissions?.includes('edit_beneficiary');
  }

  /**
   * Handle input mode change
   */
  onChangeInputMode(): void {
    this.input_value = '';
    this.searchPlaceHolder = this.input_option === 'MAT' 
      ? 'Entrez numéro matricule' 
      : 'Entrez numéro cnam';
    
    // Reset search results when changing mode
    if (this.searchAttempted) {
      this.resetBeneficiaryData();
    }
  }

  /**
   * Main search function for beneficiaries
   */
  searcheBenef(matricule?: any): void {
    this.isLoading = true;
    this.searchAttempted = true;
    
    // Prepare search parameters
    let searchData: any;
    let searchValue = matricule || this.input_value;

    // Set input option and value if matricule is provided
    if (matricule) {
      this.input_option = 'MAT';
      this.input_value = matricule;
    }

    // Validation
    if (!searchValue) {
      const messages: { [key: string]: string } = {
        CMU: 'Veuillez renseigner le numéro CMU',
        MAT: 'Veuillez renseigner le numéro matricule',
      };

      const message = messages[this.input_option];
      if (message) {
        this.utilities.showSweetNotification(message, 'error');
        this.isLoading = false;
        return;
      }
    }

    // Prepare request data based on search type
    if (this.input_option === 'CMU') {
      searchData = { numero_cnam: searchValue };
    } else {
      searchData = { matricule: searchValue };
    }

    // Reset beneficiary data
    this.itemBeneficiare = {};

    // Prepare request
    const request = {
      is_required_dossier: false,
      data: searchData,
    };

    console.log('Searching beneficiary with:', request);

    // Make API call
    this.restClient.post('beneficiaire/search', request).subscribe({
      next: (res: any) => {
        this.handleSearchSuccess(res);
      },
      error: (error) => {
        this.handleSearchError(error);
      }
    });
  }

  /**
   * Handle successful search response
   */
  private handleSearchSuccess(res: any): void {
    this.isLoading = false;
    
    if (res && !res['hasError'] && res.item) {
      this.itemBeneficiare = {
        ...res.item,
        // Ensure we have default values for image handling
        // image_url: res.item.image_url || this.getRandomDefaultImage(),
        is_online: res.item.is_online ?? false,
        display_name: this.formatDisplayName(res.item)
      };

      // Set active beneficiary in auth service
      this.authService.setActiveBeneficiaire(this.itemBeneficiare);
      
      // Emit the beneficiary info
      this.sendUserInfo.emit(this.itemBeneficiare);
      
      // Show success message
      // this.utilities.showSweetNotification(
      //   `Bénéficiaire ${this.itemBeneficiare.display_name} trouvé avec succès`,
      //   'success'
      // );
      
      console.log('Beneficiary found:', this.itemBeneficiare);
    } else {
      this.handleSearchError(res?.status?.message || 'Aucun bénéficiaire trouvé');
    }
  }

  /**
   * Handle search error
   */
  private handleSearchError(error: any): void {
    this.isLoading = false;
    
    const errorMessage = typeof error === 'string'
      ? error
      : error?.error?.status?.message || error?.message || 'Erreur lors de la recherche';
    
    this.utilities.showSweetNotification(errorMessage, 'error');
    this.resetBeneficiaryData();
    
    console.error('Search error:', error);
  }

  /**
   * Reset beneficiary data
   */
  private resetBeneficiaryData(): void {
    this.itemBeneficiare = {};
    // this.authService.setActiveBeneficiaire({});
  }

  /**
   * Format display name for beneficiary
   */
  private formatDisplayName(beneficiary: any): string {
    const prenom = beneficiary.prenom || '';
    const nom = beneficiary.nom || '';
    return `${prenom} ${nom}`.trim() || 'Nom non renseigné';
  }

  /**
   * Get a random default image
   */
  getRandomDefaultImage(): string {
    const randomIndex = Math.floor(Math.random() * this.defaultImages.length);
    return this.defaultImages[randomIndex];
  }

  /**
   * Get placeholder image SVG
   */
  getPlaceholderImage(): string {
    return `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgODBDMTEwLjQ5NCA4MCAxMTkgNzEuNDkzNCAxMTkgNjFDMTE5IDUwLjUwNjYgMTEwLjQ5NCA0MiAxMDAgNDJDODkuNTA2NiA0MiA4MSA1MC41MDY2IDgxIDYxQzgxIDcxLjQ5MzQgODkuNTA2NiA4MCAxMDAgODBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0xMDAgOTBDNzUuMTQ3MiA5MCA1NSAxMTAuMTQ3IDU1IDEzNVYxNTBIMTQ1VjEzNUMxNDUgMTEwLjE0NyAxMjQuODUzIDkwIDEwMCA5MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+`;
  }

  /**
   * Handle image loading error
   */
  onImageError(event: any): void {
    console.log('Image failed to load, using placeholder');
    // event.target.src = this.getPlaceholderImage();
    event.target.src = this.getPlaceholderImage();
    this.imageLoading = false;
  }

  /**
   * Handle image load success
   */
  onImageLoad(event: any): void {
    this.imageLoading = false;
  }

  /**
   * Get CSS class for status badge
   */
  getStatusClass(status: string): string {
    if (!status) return 'status-default';
    
    const statusClasses: { [key: string]: string } = {
      'ACTIF': 'status-active',
      'INACTIF': 'status-inactive',
      'SUSPENDU': 'status-suspended',
      'EN_ATTENTE': 'status-pending',
      'EXPIRE': 'status-expired'
    };
    
    return statusClasses[status.toUpperCase()] || 'status-default';
  }

  /**
   * Open image upload dialog
   */
  openImageUpload(): void {
    if (this.canEditProfile && this.itemBeneficiare?.id) {
      this.imageInput?.nativeElement?.click();
    } else {
      this.utilities.showSweetNotification(
        'Vous n\'avez pas les permissions pour modifier cette image',
        'warning'
      );
    }
  }

  /**
   * Handle image file selection
   */
  onImageSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.utilities.showSweetNotification(
        'Veuillez sélectionner un fichier image valide',
        'error'
      );
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      this.utilities.showSweetNotification(
        'La taille de l\'image ne doit pas dépasser 2MB',
        'error'
      );
      return;
    }

    this.imageLoading = true;
    
    // Preview the image locally
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.itemBeneficiare.image_url = e.target.result;
    };
    reader.readAsDataURL(file);

    // Upload to server
    this.uploadImageToServer(file);
  }

  /**
   * Upload image to server
   */
  private uploadImageToServer(file: File): void {
    if (!this.itemBeneficiare?.id) {
      this.utilities.showSweetNotification(
        'Aucun bénéficiaire sélectionné',
        'error'
      );
      this.imageLoading = false;
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('beneficiary_id', this.itemBeneficiare.id.toString());

    console.log('Uploading image for beneficiary:', this.itemBeneficiare.id);

    this.restClient.post('beneficiaire/upload-image', formData).subscribe({
      next: (response: any) => {
        this.imageLoading = false;
        if (response && !response.hasError) {
          this.itemBeneficiare.image_url = response.image_url;
          this.utilities.showSweetNotification(
            'Image mise à jour avec succès',
            'success'
          );
        } else {
          this.utilities.showSweetNotification(
            response?.status?.message || 'Erreur lors de l\'upload',
            'error'
          );
        }
      },
      error: (error) => {
        this.imageLoading = false;
        console.error('Error uploading image:', error);
        this.utilities.showSweetNotification(
          'Erreur lors de l\'upload de l\'image',
          'error'
        );
      }
    });
  }

  /**
   * Get initials for placeholder when no image
   */
  getInitials(): string {
    if (!this.itemBeneficiare?.nom && !this.itemBeneficiare?.prenom) {
      return 'B';
    }
    
    const nom = this.itemBeneficiare.nom || '';
    const prenom = this.itemBeneficiare.prenom || '';
    
    return `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();
  }

  /**
   * Check if beneficiary has complete data
   */
  isBeneficiaryDataComplete(): boolean {
    return !!(this.itemBeneficiare?.nom && this.itemBeneficiare?.prenom);
  }

  /**
   * Get current search mode label
   */
  get searchModeLabel(): string {
    return this.input_option === 'MAT' ? 'Matricule' : 'CMU';
  }

  /**
   * Get current search mode icon
   */
  get searchModeIcon(): string {
    return this.input_option === 'MAT' ? 'fa-id-card' : 'fa-credit-card';
  }

  /**
   * Reset search form
   */
  resetSearch(): void {
    this.input_value = '';
    this.resetBeneficiaryData();
    this.searchAttempted = false;
    this.isLoading = false;
  }

  /**
   * Handle component input changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.resetBeneficiaryData();
    // this.authService.setActiveBeneficiaire({})
    // this.itemBeneficiare={}
    console.log('changes: ',changes)
    // debugger
    this.itemBeneficiare = {}
    console.log('currentValue: ',changes['beneficiaireAutOselect']?.currentValue)
    if (changes['beneficiaireAutOselect']?.currentValue) {
      const activeBeneficiaire = this.authService.getActiveBeneficiaire();
      if (activeBeneficiaire?.matricule) {
        console.log('Auto-selecting beneficiary:', activeBeneficiaire.matricule);
        this.searcheBenef(activeBeneficiaire.matricule);
      }
    }
  }

  /**
   * Component cleanup
   */
  ngOnDestroy(): void {
    // Clean up any subscriptions if needed
  }
}